import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { CONTRACT_TEMPLATES_META, isPremium, priceFor } from "@/lib/contract-templates";
import { assertAdmin } from "@/lib/api/_admin-guard";

const VarsSchema = z
  .object({
    partyA: z
      .object({
        name: z.string().trim().max(200).optional().default(""),
        idnum: z.string().trim().max(60).optional().default(""),
        address: z.string().trim().max(400).optional().default(""),
        email: z.string().trim().max(200).optional().default(""),
        phone: z.string().trim().max(60).optional().default(""),
      })
      .optional(),
    partyB: z
      .object({
        name: z.string().trim().max(200).optional().default(""),
        idnum: z.string().trim().max(60).optional().default(""),
        address: z.string().trim().max(400).optional().default(""),
        email: z.string().trim().max(200).optional().default(""),
        phone: z.string().trim().max(60).optional().default(""),
      })
      .optional(),
    amount: z.string().trim().max(60).optional().default(""),
    startDate: z.string().trim().max(60).optional().default(""),
    endDate: z.string().trim().max(60).optional().default(""),
    date: z.string().trim().max(60).optional().default(""),
    reference: z.string().trim().max(120).optional().default(""),
    cause: z.string().trim().max(600).optional().default(""),
    purpose: z.string().trim().max(600).optional().default(""),
    premises: z.string().trim().max(400).optional().default(""),
    about: z.string().trim().max(2000).optional().default(""),
    skills: z.string().trim().max(2000).optional().default(""),
    experience: z.string().trim().max(6000).optional().default(""),
    education: z.string().trim().max(3000).optional().default(""),
  })
  .default({});


function makeRef(slug: string) {
  const prefix = slug.slice(0, 3).toUpperCase();
  const stamp = Date.now().toString(36).toUpperCase();
  const rand = Math.random().toString(36).slice(2, 6).toUpperCase();
  return `NDL-${prefix}-${stamp}-${rand}`;
}

// PUBLIC — create a premium contract order (no auth required; client-facing)
export const createContractOrder = createServerFn({ method: "POST" })
  .inputValidator((i: unknown) =>
    z
      .object({
        slug: z.string().min(1).max(80),
        clientName: z.string().trim().min(2).max(200),
        clientEmail: z.string().trim().email().max(200),
        clientPhone: z.string().trim().max(60).optional().default(""),
        vars: VarsSchema,
      })
      .parse(i),
  )
  .handler(async ({ data }) => {
    const tpl = CONTRACT_TEMPLATES_META.find((t) => t.slug === data.slug);
    if (!tpl) throw new Error("Unknown contract template");
    if (!tpl.premium) throw new Error("This contract is free — download directly.");

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { generateUnlockPassword } = await import("@/lib/pdf-protect.server");
    const reference = makeRef(data.slug);
    const unlockPassword = generateUnlockPassword();
    const { data: row, error } = await supabaseAdmin
      .from("contract_orders")
      .insert({
        slug: data.slug,
        title: tpl.title,
        client_name: data.clientName,
        client_email: data.clientEmail,
        client_phone: data.clientPhone || null,
        vars: data.vars,
        amount_zar: priceFor(data.slug),
        reference,
        status: "pending",
        unlock_password: unlockPassword,
      })
      .select("id, reference, download_token, amount_zar, status, slug, title")
      .single();
    if (error) throw new Error(error.message);

    // Proforma invoice email intentionally NOT sent on order creation.
    // The admin emails the official invoice after confirming the order
    // service, so customers go straight to PayFast checkout instead.

    return { order: row };
  });


// PUBLIC — check order status by token (used by /contracts/:slug/order/:token page)
export const getContractOrderByToken = createServerFn({ method: "POST" })
  .inputValidator((i: unknown) =>
    z.object({ token: z.string().min(20).max(120) }).parse(i),
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: row, error } = await supabaseAdmin
      .from("contract_orders")
      .select(
        "id, slug, title, client_name, amount_zar, status, reference, download_token, paid_at, created_at",
      )
      .eq("download_token", data.token)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!row) throw new Error("Order not found");
    return { order: row };
  });


// ADMIN — list orders
export const adminListContractOrders = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin
      .from("contract_orders")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(300);
    if (error) throw new Error(error.message);
    return { orders: data ?? [] };
  });

// ADMIN — mark paid / update status
export const adminUpdateContractOrder = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z
      .object({
        id: z.string().uuid(),
        status: z.enum(["pending", "paid", "cancelled"]),
        adminNotes: z.string().trim().max(2000).optional(),
      })
      .parse(i),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const isPaid = data.status === "paid";
    const { data: updated, error } = await supabaseAdmin
      .from("contract_orders")
      .update({
        status: data.status,
        admin_notes: data.adminNotes ?? null,
        paid_at: isPaid ? new Date().toISOString() : null,
        paid_by: isPaid ? context.userId : null,
      })
      .eq("id", data.id)
      .select("*")
      .single();
    if (error) throw new Error(error.message);
    if (isPaid && updated) {
      // Fire-and-await unlock notification so admin sees the result.
      const r = await sendUnlockNotification(updated);
      return { ok: true, notify: r };
    }
    return { ok: true };
  });

// ADMIN — re-send unlock password to the buyer (used after admin verifies again
// or the buyer reports they didn't receive it).
export const adminResendUnlockPassword = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z
      .object({
        id: z.string().uuid(),
        channel: z.enum(["both", "email", "sms"]).default("both"),
      })
      .parse(i),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: row, error } = await supabaseAdmin
      .from("contract_orders")
      .select("*")
      .eq("id", data.id)
      .single();
    if (error || !row) throw new Error("Order not found");
    if (row.status !== "paid") throw new Error("Order is not marked paid yet");
    const r = await sendUnlockNotification(row, data.channel);
    return { ok: true, notify: r };
  });

async function sendUnlockNotification(
  order: any,
  channel: "both" | "email" | "sms" = "both",
) {
  const { sendSystemEmail } = await import("@/lib/email/system-send.server");
  const { sendSms } = await import("@/lib/sms.server");
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

  const downloadUrl = `https://nkadimengdannylcs.com/contracts/${order.slug}/order/${order.download_token}`;
  const patch: Record<string, any> = {};

  let emailRes: any = { ok: false, reason: "skipped" };
  if (channel === "both" || channel === "email") {
    try {
      emailRes = await sendSystemEmail({
        templateName: "premium-unlock",
        recipientEmail: order.client_email,
        idempotencyKey: `unlock-${order.id}-${Date.now()}`,
        templateData: {
          clientName: order.client_name,
          documentTitle: order.title,
          reference: order.reference,
          password: order.unlock_password,
          downloadUrl,
        },
      });
    } catch (e: any) {
      emailRes = { ok: false, error: e?.message ?? String(e) };
    }
    if (emailRes?.ok) {
      patch.email_sent_at = new Date().toISOString();
      patch.email_last_error = null;
    } else {
      patch.email_last_error = emailRes?.error ?? emailRes?.reason ?? "unknown";
    }
  }

  let smsRes: any = { ok: false, reason: "skipped" };
  if (channel === "both" || channel === "sms") {
    if (!order.client_phone) {
      smsRes = { ok: false, reason: "no_phone" };
      patch.sms_last_error = "no_phone";
    } else {
      try {
        smsRes = await sendSms(
          order.client_phone,
          `NDLCS: Payment received for ${order.reference}. Open the ZIP with password ${order.unlock_password}. Link: ${downloadUrl}`,
        );
      } catch (e: any) {
        smsRes = { ok: false, error: e?.message ?? String(e) };
      }
      if (smsRes?.ok) {
        patch.sms_sent_at = new Date().toISOString();
        patch.sms_last_error = null;
      } else {
        patch.sms_last_error = smsRes?.error ?? smsRes?.reason ?? "unknown";
      }
    }
  }

  if (patch.email_sent_at || patch.sms_sent_at) {
    patch.password_sent_at = new Date().toISOString();
  }

  if (Object.keys(patch).length > 0) {
    await supabaseAdmin.from("contract_orders").update(patch as any).eq("id", order.id);
  }

  return { email: emailRes, sms: smsRes };
}


export { isPremium };

// ADMIN — backfill missing unlock passwords on any pre-existing rows so the
// admin panel never shows "no password generated" for a paid order.
export const adminBackfillUnlockPasswords = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { generateUnlockPassword } = await import("@/lib/pdf-protect.server");
    const { data: rows, error } = await supabaseAdmin
      .from("contract_orders")
      .select("id")
      .is("unlock_password", null);
    if (error) throw new Error(error.message);
    let fixed = 0;
    for (const r of rows ?? []) {
      const { error: upErr } = await supabaseAdmin
        .from("contract_orders")
        .update({ unlock_password: generateUnlockPassword() })
        .eq("id", r.id);
      if (!upErr) fixed += 1;
    }
    return { ok: true, fixed };
  });

// ADMIN — generate any premium contract PDF, UNLOCKED & FREE, for admin use.
// Admins bypass payment and password protection entirely.
export const adminGeneratePremiumPdf = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z
      .object({
        slug: z.string().min(1).max(80),
        orderId: z.string().uuid().optional(),
        vars: VarsSchema.optional(),
      })
      .parse(i),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context);
    const { findContract } = await import("@/lib/contract-templates.server");
    const { personalize } = await import("@/lib/contract-personalize.server");
    const { buildBrandedPdf } = await import("@/lib/pdf-brand.server");
    const tpl = findContract(data.slug);
    if (!tpl) throw new Error("Unknown contract template");

    let vars: any = data.vars ?? {};
    let reference: string | undefined;
    let nameBase: string | undefined;
    if (data.orderId) {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { data: row } = await supabaseAdmin
        .from("contract_orders")
        .select("vars, reference, client_name, slug")
        .eq("id", data.orderId)
        .maybeSingle();
      if (row) {
        if (row.slug !== data.slug) throw new Error("Order / slug mismatch");
        vars = row.vars ?? {};
        reference = row.reference;
        nameBase = row.client_name;
      }
    }
    const blocks = personalize(tpl.blocks, vars);
    const bytes = await buildBrandedPdf({
      title: tpl.title,
      documentLabel: tpl.documentLabel,
      reference,
      blocks,
      origin: "https://nkadimengdannylcs.com",
    });

    const safe = (nameBase || vars?.partyA?.name || tpl.slug)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "")
      .slice(0, 40);
    // Encode to base64 for transport over the RPC boundary.
    let binary = "";
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
    }
    const base64 = btoa(binary);
    return {
      ok: true,
      filename: `${tpl.slug}-${safe}-ADMIN.pdf`,
      base64,
    };
  });


